import { Alert, BottomNavigation, BottomNavigationAction, Box, Card, CardActionArea, CardContent, CardHeader, CircularProgress, Container, Divider, Grid, Link, Paper, Stack, TextareaAutosize, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { LogementPageOutletContext, ViewKind } from "../LogementPageBase";
import { AddAPhoto, ArrowBackIos, ArrowForwardIos, CancelRounded, CheckCircle, NavigateBefore, NavigateNext, NotInterested } from "@mui/icons-material";
import { useNavigate, useOutletContext } from "react-router";
import { IValueAccessor } from "../../../utils/ValueAccessor";
import { DiagnosticReponse } from "../../../dataModel/DiagnosticReponse";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { diagnosticQuestions, diagnosticQuestionnaire } from "../../../dataModel/generated/questionnaire";
import { DiagnosticQuestionKind } from "../../../dataModel/generated/DiagnosticQuestion";
import { LogementAccessor } from "../../../services/LogementAccessor";
import { VisuallyHiddenInput } from "../../../components/VisuallyHiddenInput";
import Compressor from 'compressorjs';
import axios from 'axios';

interface DiagQuestionDescription {
    readonly label: string;
    readonly kind: DiagnosticQuestionKind;
    readonly condition?: (accessor: LogementAccessor) => boolean;
    readonly scoreLevel?: 1 | 2 | 3;
}

export interface DiagnosticTabContentProps {
    readonly viewKind: ViewKind;
}

const icons = (value: DiagnosticReponse | undefined) => {
    switch (value) {
        case DiagnosticReponse.Non:
            return <CancelRounded aria-label="Non" color="error"/>;
        case DiagnosticReponse.Oui:
            return <CheckCircle aria-label="Oui" color="success"/>;
        case DiagnosticReponse.SansObjet:
            return <NotInterested aria-label="Sans objet" color="disabled"/>;
        case undefined:
        case null:
            return <></>;
        default:
            throw new Error("Not supported");
    }
};

export const DiagnosticTabContent = (props: DiagnosticTabContentProps) => {
    const { logementAccessor } = useOutletContext() as LogementPageOutletContext;
    const [currentIndex, setCurrentIndex] = useState<number | undefined>(undefined);
    const validQuestions = useMemo(() => {
        return allQuestions.reduce<ReadonlyArray<DiagQuestionDescription & { readonly id: string }>>((p, c) => {
            const q = diagnosticQuestions[c];
            if (q.condition && !q.condition(logementAccessor)) {
                return p;
            }
            return [...p, { ...q, id: c }];
        }, [])
    }, [allQuestions, diagnosticQuestions, logementAccessor]);
    const valideQuestionIds = useMemo(() => {
        return validQuestions.reduce<Readonly<Record<string, number>>>((p, c, i) => {
            return {...p, [c.id]: i };
        }, {});
    }, [validQuestions]);
    const navigate = useNavigate();
    if (typeof currentIndex === "number") {
        return <DiagnosticQuestionTabContent
            validQuestions={validQuestions}
            logementAccessor={logementAccessor}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
        />;
    }
    const goToPrevious = () => {
        navigate("./..");
    };
    const goToNext = () => {
        navigate("../comments");
    };
    return (<Box sx={{ pb: 7 }}><Stack spacing={1}>
        {props.viewKind !== ViewKind.Read &&
        <Alert severity="info">Cliquez sur la question par laquelle vous souhaitez commencer le diagnostic.</Alert>
        }
        {diagnosticSections.map((section, sectionIndex) => {
            return (
        <Card key={`section${sectionIndex}`} variant="outlined" sx={{borderWidth: 0}}>
            <CardHeader title={section.label} titleTypographyProps={{
                component: "h2",
                variant: "subtitle1",
                color: "primary"
            }} sx={{
                p: 0.4
            }} />
            {section.children.map((item, index) =>
                <div key={`section${sectionIndex}_item${index}`}><Item
                    item={item}
                    logementAccessor={logementAccessor}
                    validQuestions={validQuestions}
                    valideQuestionIds={valideQuestionIds}
                    viewKind={props.viewKind}
                    setCurrentIndex={setCurrentIndex}/></div>
            )}
        </Card>);
        })}
    </Stack>
    <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }} elevation={3}>
        <Container sx={{p: "1 1"}} maxWidth="md">
          <BottomNavigation
            showLabels
          >
            <BottomNavigationAction label="Précédent" icon={<NavigateBefore />} onClick={goToPrevious} />
            <BottomNavigationAction label="Suivant" icon={<NavigateNext />} onClick={goToNext} />
          </BottomNavigation>
        </Container>
      </Paper></Box>);
}

interface ItemProps {
    readonly item: unknown;
    readonly logementAccessor: LogementAccessor;
    readonly validQuestions: readonly (DiagQuestionDescription & { readonly id: string })[];
    readonly valideQuestionIds: Readonly<Record<string, number>>;
    readonly setCurrentIndex: React.Dispatch<React.SetStateAction<number | undefined>>
    readonly viewKind: ViewKind;
}

const Item = (props: ItemProps) => {
    const { item, logementAccessor, validQuestions, valideQuestionIds, viewKind, setCurrentIndex } = props;
    switch (typeof item) {
        case "object": {
            const condition = (item as {condition: any})["condition"];
            if (condition && !condition(logementAccessor)) {
                return <React.Fragment></React.Fragment>;
            }
            return <React.Fragment>
                <Divider/>
                <CardContent sx={{
                    p: 0.4,
                    '&:last-child': {
                        p: 0.4,
                    },
                }}>
                    <Typography component="h3" variant="subtitle2" sx={{mt: 1}}>
                        {(item as {title: string}).title}
                    </Typography>
                </CardContent>
            </React.Fragment>
        }
        case "string": {
            const questionIndex = valideQuestionIds[item];
            if (typeof questionIndex !== "number") {
                return <React.Fragment></React.Fragment>;
            }
            const q = validQuestions[questionIndex];
            const accessor = (logementAccessor as unknown as Record<string, any>)[item];
            return <>
                <Divider/>
                <CardContent sx={{
                    p: 0.4,
                    '&:last-child': {
                        p: 0.4,
                    },
                }}>
                    {q.kind === 'Texte' && <CommentCardContent
                        questionIndex={questionIndex}
                        setCurrentIndex={setCurrentIndex}
                        viewKind={viewKind}
                        title={q.label}
                        accessor={accessor}
                    />}
                    {q.kind !== 'Texte' && <QuestionCardContent
                        questionIndex={questionIndex}
                        setCurrentIndex={setCurrentIndex}
                        viewKind={viewKind}
                        title={q.label}
                        accessor={accessor}
                    />}
                </CardContent>
            </>
        }
        default:
            throw new Error("Not supported");
    }
};

interface CommentCardContentProps {
    readonly questionIndex: number;
    readonly setCurrentIndex: (newValue: number | undefined) => void;
    readonly viewKind: ViewKind
    readonly title: string | undefined;
    readonly accessor: IValueAccessor<string | undefined> | undefined;
}

const CommentCardContent = (props: CommentCardContentProps) => {
    const { questionIndex, setCurrentIndex, viewKind, title, accessor } = props;
    const handleClick = () => {
        setCurrentIndex(questionIndex);
    }
    if (viewKind !== ViewKind.Read) {
        return <CardActionArea onClick={handleClick}>
            <CommentCardContent
                questionIndex={questionIndex}
                setCurrentIndex={setCurrentIndex}
                viewKind={ViewKind.Read}
                title={title}
                accessor={accessor} />
        </CardActionArea>;
    }
    return <>
        <Typography variant="body2">
            {title}
        </Typography>
        <Box>
            <TextareaAutosize
                disabled
                minRows={7}
                value={accessor?.get() ?? ""}
                onChange={(ev) => accessor?.set(ev.target.value)}
                style={{width: "100%", resize: "none"}}
            />
        </Box>
    </>
}

interface QuestionCardContentProps {
    readonly questionIndex: number;
    readonly setCurrentIndex: (newValue: number | undefined) => void;
    readonly viewKind: ViewKind
    readonly title: string | undefined;
    readonly accessor: IValueAccessor<DiagnosticReponse | undefined> | undefined;
}

const QuestionCardContent = (props: QuestionCardContentProps) => {
    const { questionIndex, setCurrentIndex, viewKind, title, accessor } = props;
    const handleClick = () => {
        setCurrentIndex(questionIndex);
    }
    if (viewKind !== ViewKind.Read) {
        return <CardActionArea onClick={handleClick}>
            <QuestionCardContent
                questionIndex={questionIndex}
                setCurrentIndex={setCurrentIndex}
                viewKind={ViewKind.Read}
                title={title}
                accessor={accessor} />
        </CardActionArea>;
    }
    return (
    <Grid container spacing={1} alignItems="center">
        <Grid item xs={11}>
            <Typography variant="body2">
                {title?.split("\n").map(x => <>{x}<br /></>)}
            </Typography>
        </Grid>
        <Grid item xs={1} sx={{textAlign: "right"}}>
            <Typography variant="body2" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: "flex-end"
                }}>
                {icons(accessor?.get())}
            </Typography>
        </Grid>
    </Grid>);
}

interface DiagnosticSection {
  readonly label: string;
  readonly children: ReadonlyArray<string | { readonly title: string; readonly level: number; readonly condition?: (a: any) => boolean }>;
}

// Derive sections structure from questionnaire:
// - Titre1 items become section headers
// - Titre2 items become subsection headers (level 2)
// - Titre3 items become sub-subsection headers (level 3)
// - OuiNon/Texte items become question IDs
const diagnosticSections: ReadonlyArray<DiagnosticSection> = (() => {
  const sections: DiagnosticSection[] = [];
  let currentSection: DiagnosticSection | null = null;
  let currentChildren: Array<string | { readonly title: string; readonly level: number; readonly condition?: (a: any) => boolean }> = [];

  for (const q of diagnosticQuestionnaire) {
    if (q.kind === 'Titre1') {
      // Save previous section if exists
      if (currentSection) {
        sections.push({ ...currentSection, children: currentChildren });
      }
      // Start new section
      currentSection = { label: q.label, children: [] };
      currentChildren = [];
    } else if (q.kind === 'Titre2') {
      // Add as a titled item with condition and level=2 for Titre2
      let item: { readonly title: string; readonly level: number; readonly condition?: (a: any) => boolean } = {
        title: q.label,
        level: 2,
        ...(q.condition ? { condition: q.condition } : {})
      };
      if (currentSection) {
        currentChildren.push(item);
      }
    } else if (q.kind === 'Titre3') {
      let item: { readonly title: string; readonly level: number; readonly condition?: (a: any) => boolean } = {
        title: q.label,
        level: 3,
        ...(q.condition ? { condition: q.condition } : {})
      };
      if (currentSection) {
        currentChildren.push(item);
      }
    } else if (q.kind === 'OuiNon' || q.kind === 'Texte') {
      // Add question ID (only if it has an ID)
      if (q.id) {
        currentChildren.push(q.id);
      }
    }
  }

  // Save final section
  if (currentSection) {
    sections.push({ ...currentSection, children: currentChildren });
  }

  return sections;
})();

const allQuestions = diagnosticSections.reduce<string[]>((all, s) => {
    const children = s.children.reduce<string[]>((p, c) => {
        if (typeof c !== "string") {
            return p;
        }
        const question = (diagnosticQuestions as Record<string, object>)[c];
        if (!question) {
            throw new Error("Unexpected");
        }
        return [...p, c];
    }, [])
    return all.concat(children);
}, []);

const subTiltlesByQuestionId = diagnosticSections.reduce<Record<string, string[]>>((all, s) => {
    let subTitles: string[] = [];
    const children = s.children.reduce<Record<string, string[]>>((p, c) => {
        if (typeof c !== "string") {
            subTitles = subTitles.slice(0, c.level - 1);
            subTitles[c.level - 1] = c.title;
            return p;
        }
        const question = diagnosticQuestions[c];
        if (!question) {
            throw new Error("Unexpected");
        }
        if (question.kind === 'Texte') {
            subTitles = [];
        }
        return {...p, [c]: [...subTitles]};
    }, {})
    return {...all, ...children};
}, {});

const sectionIndexByQuestionId = diagnosticSections.reduce<Record<string, number>>((all, s, sectionIndex) => {
    const children = s.children.reduce<Record<string, number>>((p, c) => {
        if (typeof c !== "string") {
            return p;
        }
        const question = (diagnosticQuestions as Record<string, object>)[c];
        if (!question) {
            throw new Error("Unexpected");
        }
        return {...p, [c]: sectionIndex};
    }, {})
    return {...all, ...children};
}, {});

interface DiagnosticQuestionTabContentProps {
    readonly validQuestions: ReadonlyArray<DiagQuestionDescription  & { readonly id: string }>;
    readonly logementAccessor: LogementAccessor;
    readonly currentIndex: number;
    readonly setCurrentIndex: (newValue: number | undefined) => void;
}

const DiagnosticQuestionTabContent = (props: DiagnosticQuestionTabContentProps) => {
    const { validQuestions, logementAccessor, currentIndex, setCurrentIndex } = props;
    useEffect(() => {
        if (currentIndex !== undefined && currentIndex < 0 || currentIndex >= validQuestions.length) {
            setCurrentIndex(undefined);
        }
    }, [currentIndex]);
    if (currentIndex < 0 || currentIndex >= validQuestions.length) {
        return <></>;
    }

    const itemRender = (sectionLabel: string, item: DiagQuestionDescription & { readonly id: string }, subTitles:string[]) => {
        if (item.kind === 'Texte') {
            const accessor = (logementAccessor as unknown as Record<string, IValueAccessor<string | undefined> | undefined>)[item.id];
            return <CommentRender
                    setCurrentIndex={setCurrentIndex}
                    accessor={accessor}
                    sectionLabel={sectionLabel}
                    label={item.label}/>;
        } else {
            return <QuestionRender
                    setCurrentIndex={setCurrentIndex}
                    sectionLabel={sectionLabel}
                    subTitles={subTitles}
                    label={item.label}/>;
        }
    };

    const responseRender = (item: DiagQuestionDescription & { readonly id: string }) => {
        if (item.kind === 'Texte') {
            return <></>;
        }
        const accessor = (logementAccessor as unknown as Record<string, IValueAccessor<DiagnosticReponse | undefined> | undefined>)[item.id];
        return <ResponseButtonGroup
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                accessor={accessor}/>;
    }

    const item = validQuestions[currentIndex];
    const sectionIndex = sectionIndexByQuestionId[item.id];
    const section = diagnosticSections[sectionIndex];
    const subTitles = subTiltlesByQuestionId[item.id];

    return <>
        <div>
            {itemRender(section.label, item, subTitles)}
        </div>
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
                <div>
                    {responseRender(item)}
                </div>
            <DiagnosticNavigationBar
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                mediasValueAccessor={logementAccessor.medias}/>
        </Paper>
    </>;
}

interface CommentRenderProps {
    readonly accessor: IValueAccessor<string | undefined> | undefined;
    readonly sectionLabel: string;
    readonly label: string | undefined;
    readonly setCurrentIndex: (newValue: number | undefined) => void;
}

const CommentRender = (props: CommentRenderProps) => {
    const { accessor, sectionLabel, label } = props;
    return <Stack spacing={5}>
                    <Link
                component="a"
                onClick={() => {props.setCurrentIndex(undefined);}}
                color="inherit"
                underline="hover">
                Retour à la liste
            </Link>
        <Typography component="h2"
            variant="subtitle1"
            color="primary">
            {sectionLabel}
        </Typography>
        <Typography variant="subtitle2">
            {label?.split("\n").map(x => <>{x}<br /></>)}
        </Typography>
        <Box>
            <TextareaAutosize
                minRows={7}
                value={accessor?.get() ?? ""}
                onChange={(ev) => accessor?.set(ev.target.value)}
                style={{width: "100%", resize: "none"}}
            />
        </Box>
    </Stack>;
};

interface QuestionRenderProps {
    readonly sectionLabel: string;
    readonly label: string | undefined;
    readonly subTitles: ReadonlyArray<string>;
    readonly setCurrentIndex: (newValue: number | undefined) => void;
}

const QuestionRender = (props: QuestionRenderProps) => {
    const { /*accessor,*/ sectionLabel, label } = props;
    return <Stack spacing={5}>
        <Box>
            <Link
                component="a"
                onClick={() => {props.setCurrentIndex(undefined);}}
                color="inherit"
                underline="hover">
                Retour à la liste
            </Link>
            <Typography component="h2"
                variant="subtitle1"
                color="primary">
                {sectionLabel}
            </Typography>
            {props.subTitles.map(subTitle => {
                return <Typography variant="body1">
                    {subTitle}
                </Typography>;
            })}
        </Box>
        <Typography variant="subtitle2" fontSize={22}>
            {label?.split("\n").map(x => <>{x}<br /></>)}
        </Typography>
    </Stack>;
};

interface ResponseButtonGroupProps {
    readonly accessor: IValueAccessor<DiagnosticReponse | undefined> | undefined;
    readonly currentIndex: number;
    readonly setCurrentIndex: (index: number) => void;
}

const ResponseButtonGroup = (props: ResponseButtonGroupProps) => {
    const { accessor, currentIndex, setCurrentIndex } = props;
    return <ToggleButtonGroup
        value={accessor?.get()}
        exclusive
        size="large"
        onChange={(_, value) => {
            accessor?.set(value);
            setCurrentIndex(currentIndex + 1);
        }}
        aria-label="réponse"
        fullWidth
        sx={{
            justifyContent: "center",
        }}
    >
        <ToggleButton value={DiagnosticReponse.Oui} aria-label="oui">
            <Stack alignItems="center" spacing={1}>
                {icons(DiagnosticReponse.Oui)}
                <Typography sx={{
                    fontSize: "0.8em"
                }}>
                    Oui
                </Typography>
            </Stack>
        </ToggleButton>
        <ToggleButton value={DiagnosticReponse.Non} aria-label="non">
            <Stack alignItems="center" spacing={1}>
                {icons(DiagnosticReponse.Non)}
                <Typography sx={{
                    fontSize: "0.8em"
                }}>
                    Non
                </Typography>
            </Stack>
        </ToggleButton>
        <ToggleButton value={DiagnosticReponse.SansObjet} aria-label="sans objet">
            <Stack alignItems="center" spacing={1}>
                {icons(DiagnosticReponse.SansObjet)}
                <Typography sx={{
                    fontSize: "0.8em"
                }}>
                    Sans objet
                </Typography>
            </Stack>
        </ToggleButton>
    </ToggleButtonGroup>;
};

interface DiagnosticNavigationBarProps {
    readonly currentIndex: number;
    readonly setCurrentIndex: (index: number) => void;
    readonly mediasValueAccessor: IValueAccessor<ReadonlyArray<string>> | undefined;
}

const DiagnosticNavigationBar = (props: DiagnosticNavigationBarProps) => {
    const { currentIndex, setCurrentIndex, mediasValueAccessor } = props;
    const baseApiUrl = "/api";
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const handleFileAdd = async (file: File) => {
        setError(undefined);
        setUploading(true);
        try {
          // Bouton caméra uniquement : compression systématique avec limite de résolution
          const compressedFilePromise: Promise<FormData> = new Promise((resolve) => {
            new Compressor(file, {
              quality: 0.8, // valeur par défaut de compressorjs
              maxWidth: 2048,
              maxHeight: 2048,
              convertSize: 1048576 * 2,
              success: (compressedFile) => {
                const formData = new FormData();
                formData.append('file', compressedFile, (compressedFile as File).name);
                resolve(formData);
              }
            });
          });
          const compressedFile = await compressedFilePromise;
          const response = await axios.post<ReadonlyArray<{ id: string }>>(
            `${baseApiUrl}/Files`,
            compressedFile,
          );
          if (response.status !== 200) {
            setError("Impossible d'envoyer le fichier. Vérifiez votre connexion.");
          } else {
            const data = response.data;
            if (data.length !== 1) {
              throw new Error("Unexpected");
            }
            if (!mediasValueAccessor) {
              throw new Error("Unexpected");
            }
            mediasValueAccessor.set([...mediasValueAccessor.get(), data[0].id]);
          }
        } catch (e) {
          setError("Impossible d'envoyer le fichier. Vérifiez votre connexion.");
        } finally {
          setUploading(false);
        }
    };
    const handlePhoto = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
          handleFileAdd(files[0]);
          event.target.value = '';
        }
    };
    return <Box>
        {error && <Alert severity="error" onClose={() => setError(undefined)}>{error}</Alert>}
        <BottomNavigation>
            <BottomNavigationAction aria-label="précedent" onClick={() => setCurrentIndex(currentIndex - 1)} icon={<ArrowBackIos />} />
            {!uploading && <BottomNavigationAction disabled={!mediasValueAccessor} component="label" aria-label="photo" icon={<>
                <AddAPhoto />
                <VisuallyHiddenInput type="file" accept="image/*" capture onChange={handlePhoto} />
                </>} />}
            {uploading && <CircularProgress/>}
            <BottomNavigationAction aria-label="suivant" onClick={() => setCurrentIndex(currentIndex + 1)} icon={<ArrowForwardIos />} />
        </BottomNavigation>
    </Box>;
};
